import { isSupportedItemType } from "./../../../utils/item-type-checker";
import { ViewportObserver } from "../viewport";
import * as A from "~/parse/sfml-ast";
import * as E from "~/renderer/web-components/element";
import * as L from "~/renderer/web-components/layout";
import * as S from "~/styles/sfml.css";
import * as C from "~/constants";

export class ItemAtom extends E.SFMLCullingElement implements E.SFMLAnnotatable, E.SFMLImpressionable {
  private readonly deetContainerElement: HTMLElement;
  private readonly imageContainerElement: HTMLElement;
  private nameElement: HTMLElement | null = null;
  private prePriceElement: HTMLElement | null = null;
  private priceElement: HTMLElement | null = null;
  private postPriceElement: HTMLElement | null = null;
  private salesStoryElement: HTMLElement | null = null;
  private textElements: L.TextView[] = [];

  private flexGrow: string | null = null;
  private name: string | null = null;
  private prePrice: string | null = null;
  private price: string | null = null;
  private postPrice: string | null = null;
  private originalPrice: string | null = null;
  private salesStory: string | null = null;
  private imagePosition: string | null = null;

  private annotationElement: HTMLDivElement | null = null;

  static observedAttributes = [
    "flex-grow",
    "width",
    "height",
    "source-id",
    "name",
    "pre-price",
    "price",
    "post-price",
    "original-price",
    "sales-story",
    "margin",
    "image-position",
  ];

  public get itemType(): A.ItemType | null {
    const itemType = this.getAttribute("item-type") as A.ItemType;
    if (!itemType || !isSupportedItemType(itemType)) return null;

    return itemType;
  }

  public get targetAnchorId(): string | null {
    const targetAnchorId = this.getAttribute("target-anchor-id");

    return targetAnchorId;
  }

  public get itemId(): string | null {
    return this.getAttribute("source-id");
  }

  public insertTextElement(textElement: L.TextView) {
    this.textElements.push(textElement);
  }

  attributeChangedCallback(attr: string, oldValue: string | null, newValue: string | null) {
    switch (attr) {
      case "name": {
        this.name = newValue;
        break;
      }
      case "pre-price": {
        this.prePrice = newValue;
        break;
      }
      case "price": {
        this.price = newValue;
        break;
      }
      case "post-price": {
        this.postPrice = newValue;
        break;
      }
      case "original-price": {
        this.originalPrice = newValue;
        break;
      }
      case "sales-story": {
        this.salesStory = newValue;
        break;
      }
      case "image-position": {
        this.imagePosition = newValue;
        break;
      }
      case "flex-grow": {
        this.flexGrow = newValue;
        break;
      }
    }
  }

  public handleVisible() {
    super.handleVisible();
    this.render();
    if (this.textElements.length === 0) this.draw();
  }

  protected handleHidden() {
    super.handleHidden();
    this.unrender();
  }

  private render() {
    if (this.textElements.length === 0) {
      if (
        this.nameElement &&
        this.prePriceElement &&
        this.priceElement &&
        this.postPriceElement &&
        this.salesStoryElement
      )
        return;

      this.setAttribute("role", "button");
      this.setAttribute("tabindex", "0");

      this.nameElement = document.createElement("div");
      this.prePriceElement = document.createElement("div");
      this.priceElement = document.createElement("div");
      this.postPriceElement = document.createElement("div");
      this.salesStoryElement = document.createElement("div");
      const priceContainerElement = document.createElement("div");

      this.nameElement.className = S.name;
      this.prePriceElement.className = S.prePrice;
      this.priceElement.className = S.price;
      this.postPriceElement.className = S.postPrice;
      this.salesStoryElement.className = S.salesStory;
      priceContainerElement.className = S.priceContainer;

      this.deetContainerElement.appendChild(this.nameElement);
      this.deetContainerElement.appendChild(priceContainerElement);
      priceContainerElement.appendChild(this.salesStoryElement);
      priceContainerElement.appendChild(this.prePriceElement);
      priceContainerElement.appendChild(this.priceElement);
      priceContainerElement.appendChild(this.postPriceElement);
    }

    let textContainerElement = document.createElement("div");
    this.textElements.forEach(function (element) {
      textContainerElement.appendChild(element);
    });
    this.deetContainerElement.appendChild(textContainerElement);

    if (this.imagePosition === "image-left") {
      this.style.display = "flex";
      this.style.justifyContent = "center";
      this.style.alignItems = "center";

      this.imageContainerElement.style.flexBasis = "60%";
      this.deetContainerElement.className = "";
    }
    else if (this.imagePosition === "image-right") {
      this.style.display = "flex";
      this.style.flexDirection = "row-reverse";
      this.style.justifyContent = "center";
      this.style.alignItems = "center";

      this.imageContainerElement.style.flexBasis = "60%";
      this.deetContainerElement.className = "";
      this.deetContainerElement.style.paddingLeft = "10px";
    }

    if (this.flexGrow) {
      this.style.flexGrow = this.flexGrow;
    }
  }

  private draw() {
    if (
      !this.nameElement ||
      !this.prePriceElement ||
      !this.priceElement ||
      !this.postPriceElement ||
      !this.salesStoryElement
    )
      return;

    this.nameElement.textContent = this.name;

    if (this.prePrice && this.prePrice.length > 0) this.prePriceElement.textContent = this.prePrice + " ";
    else this.prePriceElement.textContent = "";

    this.priceElement.textContent = this.price;

    if (this.postPrice && this.postPrice.length > 0) this.postPriceElement.textContent = " " + this.postPrice;
    else this.postPriceElement.textContent = "";

    this.salesStoryElement.textContent = this.salesStory;
  }

  private unrender() {
    this.nameElement = null;
    this.prePriceElement = null;
    this.priceElement = null;
    this.postPriceElement = null;
    this.salesStoryElement = null;

    this.deetContainerElement.innerHTML = "";
  }

  private onClickHandler = (e: Event) => {
    if (e.type !== "click" && (e as any).keyCode !== 13) return;

    e.preventDefault();

    const itemType = this.getAttribute("item-type") as A.ItemType;
    if (!itemType || !isSupportedItemType(itemType)) return;

    const itemId = this.getAttribute("source-id");
    if (!itemId) return;

    const targetAnchorId = this.getAttribute("target-anchor-id");

    const payload: { [key: string]: string } = {};
    for (let i = 0, n = this.attributes.length; i < n; ++i) {
      const attr = this.attributes[i];
      if (attr.name.startsWith(A.PAYLOAD_PREFIX)) {
        payload[attr.name.slice(A.PAYLOAD_PREFIX.length)] = attr.value;
      }
    }

    const event = new CustomEvent(C.ITEM_CLICK_EVENT, {
      bubbles: true,
      detail: { itemType, itemId, targetAnchorId, payload },
    });
    this.dispatchEvent(event);
  };

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener("keydown", this.onClickHandler);
    this.addEventListener("click", this.onClickHandler);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener("keydown", this.onClickHandler);
    this.removeEventListener("click", this.onClickHandler);
  }

  constructor() {
    super();

    this.setAttribute("annotatable", "");

    this.deetContainerElement = document.createElement("div");

    this.deetContainerElement.className = S.deetContainer;

    this.imageContainerElement = document.createElement("div");
    this.imageContainerElement.style.position = "relative";
    this.addSlot(this.imageContainerElement);

    this.appendChildToShadow(this.imageContainerElement);
    this.appendChildToShadow(this.deetContainerElement);

    this.setAttribute("impressionable", "");
  }

  public annotate(imageUrl: string) {
    this.annotationElement = document.createElement("div");
    this.annotationElement.style.backgroundImage = `url(${imageUrl})`;
    this.annotationElement.classList.add(S.annotation);
    this.annotationElement.classList.add(`flipp-${this.itemType}-annotation`);
    this.appendChild(this.annotationElement);
  }

  public isAnnotated() {
    return !!this.annotationElement;
  }

  public clearAnnotation() {
    if (this.annotationElement) {
      const parentNode = this.annotationElement.parentNode;

      if (parentNode) parentNode.removeChild(this.annotationElement);
    }

    this.annotationElement = null;
  }

  public clientRects(): { identifier: A.ItemIdentifier; rect: ClientRect }[] {
    if (this.itemType === null || this.itemId === null) return [];

    const rect = ViewportObserver.shared.getBoundingClientRect(this);

    const identifier = {
      itemType: this.itemType,
      itemId: this.itemId,
    };
    return [{ identifier, rect }];
  }
}
customElements.define("sfml-item-atom", ItemAtom);

// export * from "../badge/badge";



// WEBPACK FOOTER //
// webpack://SFMLWeb/src/renderer/web-components/item-atom/index.ts